import { Text } from '@belong/ui';
import { IconDots } from '@belong/icons';
import type {
  ConsumerFailedPaymentModel,
  ConsumerPaymentMethodSummaryModel,
} from './failed-payments.types';
import { PaymentMethodIcon } from 'components/PaymentMethodIcon/payment-method-icon';
import { getFeeFromPaymentMethod } from 'models/utilities/paymentMethods';

type PaymentMethodSummaryProps = {
  failedPayment?: ConsumerFailedPaymentModel;
  paymentMethod?: ConsumerPaymentMethodSummaryModel | null;
  compact?: boolean;
  showMenu?: boolean;
  onChangePaymentMethod?: () => void;
};

export function PaymentMethodSummary({
  failedPayment,
  paymentMethod: paymentMethodProp,
  compact = false,
  showMenu = false,
  onChangePaymentMethod,
}: PaymentMethodSummaryProps) {
  const paymentMethod = paymentMethodProp ?? failedPayment?.paymentMethod;

  if (!paymentMethod) {
    return (
      <Text variant="body" className="text-navy">
        —
      </Text>
    );
  }

  const method =
    paymentMethod.paymentMethod === 'Unknown'
      ? undefined
      : paymentMethod.paymentMethod;
  const provider = paymentMethod.provider ?? 'Payment Method';
  // For ACH the backend returns the bank name in `provider`; for CreditCard
  // it returns the card brand. The icon component handles both.
  const bankName =
    paymentMethod.paymentMethod === 'Ach' ? paymentMethod.provider : null;
  const cardType =
    paymentMethod.paymentMethod === 'CreditCard'
      ? (paymentMethod.provider ?? 'Unknown')
      : null;
  const ending = paymentMethod.lastDigits
    ? `Ending ${paymentMethod.lastDigits}`
    : '';
  const methodLabel =
    paymentMethod.paymentMethod === 'Ach'
      ? 'Checking Account'
      : paymentMethod.paymentMethod === 'CreditCard'
        ? 'Card'
        : '';
  const feeLabel =
    paymentMethod.paymentMethod === 'Ach' ||
    paymentMethod.paymentMethod === 'CreditCard'
      ? getFeeFromPaymentMethod(paymentMethod.paymentMethod, true)
      : null;

  if (compact) {
    return (
      <div className="flex items-center gap-sm text-navy">
        <PaymentMethodIcon
          paymentMethod={method}
          bankName={bankName}
          cardType={cardType}
        />
        <Text variant="body" className="text-navy">
          {provider}
          {paymentMethod.lastDigits
            ? ` ending in ${paymentMethod.lastDigits}`
            : ''}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-md">
        <PaymentMethodIcon
          paymentMethod={method}
          bankName={bankName}
          cardType={cardType}
        />
        <div className="flex flex-col gap-2xs">
          <Text fontWeight="semibold" className="text-navy">
            {provider}
          </Text>
          {ending && (
            <Text variant="p2" className="text-navy">
              {methodLabel ? `${methodLabel} ${ending}` : ending}
              {feeLabel && (
                <>
                  {' · '}
                  <span className="font-semibold text-green">{feeLabel}</span>
                </>
              )}
            </Text>
          )}
        </div>
      </div>
      {showMenu && (
        <button
          type="button"
          aria-label="Change payment method"
          className="cursor-pointer border-0 bg-transparent p-0 text-navy"
          onClick={onChangePaymentMethod}
        >
          <IconDots width={5} height={24} />
        </button>
      )}
    </div>
  );
}
